/**
 * Data-hook voor de weergave **per gebouw** van de helpdesktickets.
 *
 * De tegenhanger van `useRenoTicketGroups` bij de renovatietickets (BUG-635):
 * daar per renovatieproject en -onderdeel, hier per **gebouw** en **unit**.
 * Groeperen, sorteren, filteren én de tellers gebeuren backend-side
 * (`GET /helpdesk/tickets/grouped`); deze hook slaat de boom enkel plat naar
 * rijen met groepeersleutels, want UniversalTable verwacht een vlakke lijst.
 *
 * Deze weergave pagineert niet — je moet een gebouw in één keer kunnen open- en
 * dichtklikken — vandaar een ticketplafond. Wordt dat bereikt, dan is
 * `truncated` true en moet de gebruiker verder filteren. De **tellers** komen
 * uit de aggregatie en blijven in dat geval gewoon correct.
 *
 * @module Helpdesk/Shared/Hooks/UseHelpdeskTicketGroups
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import type { HelpdeskFilters } from "./useHelpdeskDataLazy";
import { buildHelpdeskTicketParams } from "../utils/helpdeskTicketParams";

/** Maximaal aantal tickets dat de gegroepeerde weergave in één keer ophaalt. */
export const HELPDESK_GROUPED_TICKET_LIMIT = 1000;

export interface HelpdeskUnitGroup {
  unitId: string | null;
  unitName: string | null;
  ticketCount: number;
  openCount: number;
  completedCount: number;
  tickets: any[];
  truncated: boolean;
}

export interface HelpdeskBuildingGroup {
  buildingId: string | null;
  buildingName: string | null;
  ticketCount: number;
  openCount: number;
  completedCount: number;
  units: HelpdeskUnitGroup[];
}

export interface HelpdeskGroupedResponse {
  groups: HelpdeskBuildingGroup[];
  total: number;
  openCount: number;
  completionCount: number;
  loaded: number;
  ticketLimit: number;
  truncated: boolean;
}

/** Groepeersleutels — `null` (geen gebouw/unit) krijgt een eigen, vaste sleutel. */
export const helpdeskBuildingGroupKey = (buildingId: string | null) =>
  `building:${buildingId ?? "none"}`;
export const helpdeskUnitGroupKey = (
  buildingId: string | null,
  unitId: string | null,
) => `${helpdeskBuildingGroupKey(buildingId)}|unit:${unitId ?? "none"}`;

const EMPTY_RESPONSE: HelpdeskGroupedResponse = {
  groups: [],
  total: 0,
  openCount: 0,
  completionCount: 0,
  loaded: 0,
  ticketLimit: HELPDESK_GROUPED_TICKET_LIMIT,
  truncated: false,
};

interface UseHelpdeskTicketGroupsOptions {
  filters: HelpdeskFilters;
  /** Zet op false zolang de weergave niet actief is; dan wordt er niets opgehaald. */
  enabled?: boolean;
}

export function useHelpdeskTicketGroups({
  filters,
  enabled = true,
}: UseHelpdeskTicketGroupsOptions) {
  const { session } = useSession();
  const [data, setData] = useState<HelpdeskGroupedResponse>(EMPTY_RESPONSE);
  const [isLoading, setIsLoading] = useState(false);

  const filterParams = useMemo(
    () => buildHelpdeskTicketParams(filters),
    [filters],
  );
  const filterKey = useMemo(() => JSON.stringify(filterParams), [filterParams]);

  const fetchGroups = useCallback(async () => {
    if (!session || !enabled) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        includeEmailCounts: "true",
        limit: String(HELPDESK_GROUPED_TICKET_LIMIT),
        ...filterParams,
      });
      // Zelfde standaard als de vlakke lijst: open + in afronding zolang de
      // gebruiker zelf geen fase koos. Anders zou van weergave wisselen stil een
      // andere selectie tonen.
      if (!params.has("status")) params.set("status", "open,completion");

      const res = await fetchWithAuth(
        `${API_URL}/helpdesk/tickets/grouped?${params}`,
        { method: "GET" },
        session,
      );
      if (!res.ok) throw new Error("Ophalen van tickets per gebouw mislukt");

      const json = (await res.json()) as HelpdeskGroupedResponse;
      setData({ ...EMPTY_RESPONSE, ...json, groups: json.groups ?? [] });
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { module: "helpdesk", view: "grouped-by-building" },
        extra: { filters },
      });
      toast.error(err.message || "Fout bij ophalen van tickets per gebouw");
      setData(EMPTY_RESPONSE);
    } finally {
      setIsLoading(false);
    }
    // `filterKey` i.p.v. `filterParams`: het object is elke render nieuw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, enabled, filterKey]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  /**
   * Vlakke rijen met groepeersleutels, in de volgorde die de backend bepaalde.
   * `groupOrder` houdt die volgorde vast: UniversalTable sorteert de rijen vóór
   * het groeperen, en zonder deze index zouden de gebouwen op hun nieuwste
   * ticket komen te staan in plaats van op gebouwcode.
   */
  const rows = useMemo(() => {
    let order = 0;
    return data.groups.flatMap((group) =>
      group.units.flatMap((unit) =>
        unit.tickets.map((ticket) => ({
          ...ticket,
          buildingGroupKey: helpdeskBuildingGroupKey(group.buildingId),
          unitGroupKey: helpdeskUnitGroupKey(group.buildingId, unit.unitId),
          groupOrder: order++,
        })),
      ),
    );
  }, [data.groups]);

  const groupsByKey = useMemo(() => {
    const map = new Map<string, HelpdeskBuildingGroup>();
    data.groups.forEach((group) =>
      map.set(helpdeskBuildingGroupKey(group.buildingId), group),
    );
    return map;
  }, [data.groups]);

  const unitsByKey = useMemo(() => {
    const map = new Map<string, HelpdeskUnitGroup>();
    data.groups.forEach((group) =>
      group.units.forEach((unit) =>
        map.set(helpdeskUnitGroupKey(group.buildingId, unit.unitId), unit),
      ),
    );
    return map;
  }, [data.groups]);

  return {
    groups: data.groups,
    groupsByKey,
    unitsByKey,
    rows,
    total: data.total,
    openCount: data.openCount,
    completionCount: data.completionCount,
    loaded: data.loaded,
    ticketLimit: data.ticketLimit,
    truncated: data.truncated,
    isLoading,
    refetch: fetchGroups,
  };
}
